/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */

import { getBaseAPIMUrl, getMSALToken, getPortalHost, isIFrame } from '@jda/lui-portal-utilities';
import { getAuthToken } from '../auth/authProvider';
import jwtDecode from 'jwt-decode';
import { LOCAL_STORAGE_LAST_CONNECTION, LOCAL_STORAGE_TIMEOUT_OPT_IN } from '../byauth/constants';
import { AuthService } from '../byauth/authService';
import { DOMAIN_NAME, RND_DOMAIN, CUSTOMER_DOMAIN } from './constants';

export const apiRequestScopeVal = (scope: any[]) => scope;

export const getAPIResponse = (apiResponse: any, endpoint: string) => {
  let apiResp = [];
  if (endpoint.includes('/v1')) {
    apiResp = Boolean(apiResponse) && apiResponse.data ? apiResponse.data : [];
  } else if (endpoint.includes('/v2')) {
    apiResp = Boolean(apiResponse) && apiResponse.entities ? apiResponse.entities : [];
  } else if (endpoint.includes('localhost')) {
    apiResp = Boolean(apiResponse) && apiResponse.entities ? apiResponse.entities : [];
  }
  return apiResp;
};

export const getCount = (apiResponse: any, endpoint: string) => {
  let apiResp = [];
  if (endpoint.includes('/v1')) {
    apiResp = Boolean(apiResponse) && apiResponse.data ? apiResponse.data.total : [];
  } else if (endpoint.includes('/v2')) {
    apiResp = Boolean(apiResponse) && apiResponse.meta ? apiResponse.meta.page.totalCount : [];
  } else if (endpoint.includes('localhost')) {
    apiResp = Boolean(apiResponse) && apiResponse.meta ? apiResponse.meta.page.totalCount : [];
  }
  return apiResp;
};

export const convertObjectToQueryString = (params: any) =>
  params && typeof params === 'object'
    ? Object.keys(params)
      .map(key => `${key}=${params[key]}`)
      .join('&')
    : '';
export const getPortalToken = async (): Promise<any> => {
  if (isIFrame()) {
    return getMSALToken();
  } else {
    return isPortalV2() ? AuthService.getInstance().getAuthToken() : getAuthToken();
  }
};
export const createRequestObject = (requestUrl: string) => {
  const request = {
    url: requestUrl
  };
  if (!isIFrame()) {
    request['tokenProvider'] = isPortalV2() ? AuthService.getInstance().getAuthToken : getAuthToken;
  }
  return request;
};

export const compareByProperty = (key: string = 'name') => {
  return function (a: any, b: any) {
    a = a?.[key]?.toLowerCase();
    b = b?.[key]?.toLowerCase();
    if (a === 'select' || b === 'select') return 0;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  };
};

export const isValidEmail = (email: string) => {
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  return emailRegex.test(email);
};

export const isValidUserName = (userName: string) => {
  //Should allow only alphabets, numbers and the charecters from this set [.!#$%&'*+/=?^_`{|}~-]
  const userNameRegex = /^[a-zA-Z0-9]+[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]*$/;
  return userNameRegex.test(userName);
};

export const getUserTokenData = async () => {
  const { accessToken } = await getPortalToken();
  return jwtDecode<any>(accessToken);
};

export const getUserTokenRealmId = async () => {
  const subjectId: { by_realm_id: string } = await getUserTokenData();
  return subjectId.by_realm_id;
};
export const getAPIMUrl = async () => {
  if (isIFrame()) {
    const apimUrl = await getBaseAPIMUrl();
    return apimUrl;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const realmKey = urlParams.get('realmKey');
  if (!realmKey) {
    // if no realmKey, means it's B2C realm then we are setting BASE_APIM_URL to B2C_BASE_APIM_URL
    // for b2c APIM URLs
    window['env'].BASE_APIM_URL = window['env'].BASE_APIM_URL_B2C;
  } else {
    // if there is realmKey, we are replacing the BASE_APIM_URL_GRAVITEE with the BASE_APIM_URL
    // for gravitee APIM URLs
    let apimUrl = window['env'].BASE_APIM_URL_GRAVITEE;
    if (apimUrl.includes('byiam-dev15')) {
      apimUrl = apimUrl.replaceAll('byiam-dev15', realmKey);
    } else {
      apimUrl = apimUrl.replace(/liam-[^.]+/, realmKey);
    }
    window['env'].BASE_APIM_URL = apimUrl;
  }
  return window['env'].BASE_APIM_URL;
};
export const getUserIdFromUrl = (searchString: string) => {
  let userId: string = '';
  let urlString: string = searchString.replace('/userDetail/', '');
  if (urlString) {
    userId = urlString.split('=')[1];
  }
  return userId;
};

export const getGroupIdentifier = (searchString: string) => {
  let userGroupIdentifier: string = '';
  userGroupIdentifier = searchString.replace('/userGroup', '').replace('/', '');
  return {
    userGroupIdentifier: userGroupIdentifier || ''
  };
};

export const isPortalV2 = (): boolean => {
  if (isIFrame()) {
    const hostName = getPortalHost()
      .replace('https://', '')
      .replace('http://', '');
    const urlParts = hostName.split('.');
    if (urlParts.length === 6) {
      const [realm, service, runtime, geography, domainName, domain] = urlParts;
      if (domainName === DOMAIN_NAME && (domain === RND_DOMAIN || domain === CUSTOMER_DOMAIN) && service === 'portal') {
        console.info('Portal V2 detected', realm, service, runtime, geography, domainName, domain);
        return true;
      }
    } else if (urlParts.length === 5) {
      // To handle for relams created before the runtime was added to the URL
      // Example: byiam-dev15.portal.na.blueyonder.dev/
      const [realm, service, geography, domainName, domain] = urlParts;
      if (domainName === DOMAIN_NAME && (domain === RND_DOMAIN || domain === CUSTOMER_DOMAIN) && service === 'portal') {
        console.info('Portal V2 detected', realm, service, geography, domainName, domain);
        return true;
      }
    } else {
      return false;
    }
    return false;
  } else {
    // If not in an iframe, we assume it's Portal V2
    const urlParams = new URLSearchParams(window.location.search);
    const realmKey = urlParams.get('realmKey');
    if (!realmKey) {
      return false;
    }
    return true;
  }
};

export const removeTimeoutOtpFromStorage = () => {
  localStorage.removeItem(LOCAL_STORAGE_TIMEOUT_OPT_IN);
  localStorage.removeItem(LOCAL_STORAGE_LAST_CONNECTION);
};
